import moment from "moment"
import fp from "lodash/fp"
import * as R from "ramda"
import { createSelector } from "reselect"
import { getValues } from "redux-form"

import { getAdvices } from "../../advices/reducer"
import { RESERVATION_PART_CONFIG } from "../../../scenes/ReservationForm/consts/reservationConsts"
import { getJourney } from "../../journey/reducer"
import { getCountriesMap } from "common/services/staticData"
import { getLanguage } from "common/services/options/reducer"
import constants from "common/consts/config"
import { getShadowJourney, isShadowJourneySet } from "../../shadowJourney/reducer"
import { isInsuranceEnabledSelector } from "common/services/branding/services"
import { brandingNameSelector } from "common/services/branding/selectors"
import { isInsuranceAllowedForNationality } from "../../../../../app/scripts/tools/insurance"
import {
  bannerTextFormatter,
  tooltipAirportTextFormatter,
  tooltipCountryTextFormatter,
} from "../helpers/selfTransferHelpers"
import { getGracePeriodFee } from "../../prices/reducer"
// Keep this selector file for simple selectors related to whole booking
// Do not import other selectors except of the get ones in reducers
// ===

export const reservationFormSelector = (state: any) => state.form.reservation

export const reservationFormValuesSelector = createSelector(
  reservationFormSelector,
  reservationForm => getValues(reservationForm),
)

export const contactFormValuesSelector = createSelector(
  reservationFormValuesSelector,
  fp.get("contact"),
)

export const paymentSpecialInfoFormValuesSelector = createSelector(
  reservationFormValuesSelector,
  fp.get("paymentSpecialInfo"),
)

export const billingDetailsFormValuesSelector = createSelector(
  reservationFormValuesSelector,
  fp.get("billingDetails"),
)

export const isInsuranceEnabledInReservationSelector = createSelector(
  reservationFormValuesSelector,
  isInsuranceEnabledSelector,
  (reservationFormValues, isInsuranceEnabled) => {
    if (reservationFormValues) {
      return (
        isInsuranceEnabled &&
        reservationFormValues.passengers.some(x => isInsuranceAllowedForNationality(x.nationality))
      )
    }

    return isInsuranceEnabled
  },
)

export const containsCubaSelector = createSelector(
  getJourney,
  journey =>
    journey // journey maybe not in store
      ? journey.getCountries().includes("cu")
      : false,
)

// Special reservation where we must communicate hidden flight policy
export const isHiddenFlightReservationSelector = isShadowJourneySet
export const getHiddenFlightReservationType = createSelector(
  getShadowJourney,
  shadowJourney => {
    if (shadowJourney) {
      return shadowJourney.getHiddenFlightsType()
    }
    return null
  },
)

export const visaRequiredSelector = createSelector(
  getAdvices,
  advices => !advices || advices.size > 0,
)

export const isEligibleForGracePeriodSelector = createSelector(
  getGracePeriodFee,
  getLanguage,
  getJourney,
  (gracePeriodFee, language, journey) =>
    // received fee from BE
    gracePeriodFee &&
    // Due to legislation, only US version
    language === "us" &&
    // Departure in 7 days or after
    journey
      .getDeparture()
      .getIn(["when", "utc"])
      .isAfter(moment().add(7, "days")),
)

export const isEligibleForRefundPolicySelector = createSelector(
  isEligibleForGracePeriodSelector,
  isEligibleForGracePeriod => !isEligibleForGracePeriod,
)

export const hasLayoverAirportChangeSelector = createSelector(
  getJourney,
  journey => journey && journey.hasLayoverAirportChange(),
)

export const containsFlightSelector = createSelector(
  getJourney,
  journey => journey && journey.getTypes().includes(RESERVATION_PART_CONFIG.flight.type),
)

export const containsTrainSelector = createSelector(
  getJourney,
  journey => journey && journey.hasTrain(),
)

export const containsBusSelector = createSelector(
  getJourney,
  journey => journey && journey.hasBus(),
)

export const isTrainFlightCombinationSelector = createSelector(
  containsFlightSelector,
  containsTrainSelector,
  (containsFlight, containsTrain) => containsFlight && containsTrain,
)

const flightTransportOnly = {
  title: __("booking.airport_change_banner.title"),
  subtitle: __("booking.airport_change_banner.subtitle"),
}

const flightWithGroundTransport = {
  title: __("booking.station_change_banner.title"),
  subtitle: __("booking.station_change_banner.subtitle"),
}

export const getAirportChangeKeySelector = createSelector(
  containsFlightSelector,
  containsTrainSelector,
  containsBusSelector,
  (containsFlight, containsTrain, containsBus) => {
    return containsFlight && (containsTrain || containsBus)
      ? flightWithGroundTransport
      : flightTransportOnly
  },
)

export const containsCubaRegulationFlightSelector = createSelector(
  getJourney,
  journey => journey.getFlights().some(x => x.isCubaRegulationAffected()),
)

/**
 * Returns List of hours (numbers) for every flight
 * until it's acceptable to make booking from now
 */
export const spareCloseBookingHoursSelector = createSelector(
  getJourney,
  journey =>
    journey.getFlights().map(flight => flight.getHoursToDeparture() - flight.getClosingHours()),
)

const getMinCloseBookingHours = createSelector(
  spareCloseBookingHoursSelector,
  spareCloseBookingHours => spareCloseBookingHours.min(),
)

export const hoursUntilJourneyDepartureSelector = createSelector(
  getJourney,
  journey => journey && journey.getTimeUntil("hours"),
)

// Think about general airport notes abstraction in case of more cases
export const hasDubaiAirportSelector = createSelector(
  getJourney,
  journey => journey && journey.hasAirport("DXB"),
)

export const reservationAllowedSelector = createSelector(
  getMinCloseBookingHours,
  minCloseBookingHours => minCloseBookingHours > 0,
)

export const reservationAllowedUntilSelector = createSelector(
  getMinCloseBookingHours,
  minCloseBookingHours => moment.utc().add(minCloseBookingHours, "hours"),
)

export const departingSoonSelector = createSelector(
  reservationAllowedSelector,
  hoursUntilJourneyDepartureSelector,
  (reservationAllowed, hoursUntilDeparture) =>
    reservationAllowed && hoursUntilDeparture <= constants.soonDepartureThresholdHours,
)

export const selfTransferEnabledSelector = createSelector(
  state => state.splitster,
  splitster => splitster.get("SELF_TRANSFER").value === "shown",
)

export const selfTransferStopsSelector = createSelector(
  getJourney,
  journey => journey.getStops().filter(stop => stop.get("hasGuarantee")),
)

export const finalDestinationsSelector = createSelector(
  getJourney,
  getCountriesMap,
  (journey, countries) => {
    const trips = journey.get("trips")
    const finalDestinations = trips.map(trip => trip.getArrival().get("where")).toArray()
    const departure = trips
      .first()
      .getDeparture()
      .get("where")

    if (
      journey.isReturn() &&
      bannerTextFormatter(departure, countries) ===
        bannerTextFormatter(R.last(finalDestinations), countries)
    ) {
      return R.init(finalDestinations)
    }

    return finalDestinations
  },
)

export const selfTransferTooltipSelector = createSelector(
  [selfTransferStopsSelector, getCountriesMap, brandingNameSelector],
  (stops, countryNames, brandName) => {
    const pureAirportStops = fp.compose(
      fp.uniq,
      fp.map(stop => tooltipAirportTextFormatter(stop)),
    )(stops)
    const pureCountryStops = fp.compose(
      fp.uniq,
      fp.map(stop => tooltipCountryTextFormatter(stop, countryNames)),
    )(stops)

    const brands = { ...brandName, companyName: brandName }

    if (pureAirportStops.length > 1 && pureCountryStops.length > 1) {
      return [
        {
          key: __("multi_modal.self_transfer.tooltip.unique_itinerary"),
          values: {},
        },
        {
          key: __("multi_modal.self_transfer.tooltip.connection.Xairports_Xcountries"),
          values: {
            airports: R.init(pureAirportStops).join(", "),
            lastAirport: R.last(pureAirportStops),
            countries: R.init(pureCountryStops).join(", "),
            lastCountry: R.last(pureCountryStops),
            ...brands,
          },
        },
        {
          key: __("multi_modal.self_transfer.tooltip.layover_time"),
          values: brands,
        },
      ]
    }
    if (pureAirportStops.length > 1 && pureCountryStops.length === 1) {
      return [
        {
          key: __("multi_modal.self_transfer.tooltip.unique_itinerary"),
          values: {},
        },
        {
          key: __("multi_modal.self_transfer.tooltip.connection.Xairport_1country"),
          values: {
            airports: R.init(pureAirportStops).join(", "),
            lastAirport: R.last(pureAirportStops),
            country: R.last(pureCountryStops),
          },
        },
        {
          key: __("multi_modal.self_transfer.tooltip.layover_time"),
          values: brands,
        },
      ]
    }
    if (pureAirportStops.length === 1 && pureCountryStops.length > 1) {
      return [
        {
          key: __("multi_modal.self_transfer.tooltip.unique_itinerary"),
          values: {},
        },
        {
          key: __("multi_modal.self_transfer.tooltip.connection.Xairport_1country"),
          values: {
            airport: R.last(pureAirportStops),
            countries: R.init(pureCountryStops).join(", "),
            lastCountry: R.last(pureCountryStops),
          },
        },
        {
          key: __("multi_modal.self_transfer.tooltip.layover_time"),
          values: brands,
        },
      ]
    }
    if (pureAirportStops.length === 1 && pureCountryStops.length === 1) {
      return [
        {
          key: __("multi_modal.self_transfer.tooltip.unique_itinerary"),
          values: {},
        },
        {
          key: __("multi_modal.self_transfer.tooltip.connection.Xairport_1country"),
          values: {
            airports: pureAirportStops.join(", "),
            lastAirport: R.last(pureAirportStops),

            airport: R.last(pureAirportStops),
            country: R.last(pureCountryStops),
            ...brands,
          },
        },
        {
          key: __("multi_modal.self_transfer.tooltip.layover_time"),
          values: brands,
        },
      ]
    }
    return []
  },
)

export const selfTransferBannerTranslationsSelector = createSelector(
  [selfTransferStopsSelector, finalDestinationsSelector, getCountriesMap],
  (transfers, destinations, countries) => {
    const pureTransfers = fp.compose(
      fp.uniq,
      fp.map(stop => bannerTextFormatter(stop, countries)),
    )(transfers)

    const pureDestinations = fp.compose(
      fp.uniq,
      fp.map(stop => bannerTextFormatter(stop, countries)),
    )(destinations)

    const translations = {
      destination: {
        key: __("booking.global.self_transfer.banner.destination"),
        values: { finalDestination: fp.head(pureDestinations, countries) },
      },
    }

    if (pureTransfers.length > 1) {
      // $FlowFixMe
      translations.transfer = {
        key: __("multi_modal.self_transfer.visa_alert.transit.multiple"),
        values: {
          transferDestinations: R.init(pureTransfers).join(", "),
          lastTransferDestination: R.last(pureTransfers),
        },
      }
    } else if (pureTransfers.length > 0) {
      // $FlowFixMe
      translations.transfer = {
        key: __("multi_modal.self_transfer.visa_alert.transit.one"),
        values: {
          transferDestination: R.last(pureTransfers),
        },
      }
    }
    if (pureDestinations.length > 1) {
      translations.destination.key = __("booking.global.self_transfer.banner.destination_multiple")
      // $FlowFixMe
      translations.destination.values = {
        finalDestinations: R.init(pureDestinations).join(", "),
        lastFinalDestination: R.last(pureDestinations),
      }
    }
    return translations
  },
)
